import { inject } from 'vue'
import { NotificationSocketService } from '@nsftx/seven-client-sdk-notifications'
import type NotificationEventConfig from '#src/interfaces/NotificationEventConfig'
import type { NotificationPayload, NotificationTopic } from '#src/interfaces/ngNotifications'
import { SevenIgamingPlatformKey } from '#src/sevenIgamingPlugin'
import type { SevenIgamingPlatformConfig } from '#src/interfaces/sevenIgamingPlatform'

let notificationService: NotificationSocketService | undefined

const dispatchNotificationEvent = <TTopic extends NotificationTopic>(
  topic: TTopic,
  message: NotificationPayload<TTopic>
) => {
  window.dispatchEvent(new CustomEvent<NotificationPayload<TTopic>>(topic, { detail: message }))
}

export const useNotification = () => {
  const platformConfig = inject<SevenIgamingPlatformConfig>(SevenIgamingPlatformKey)

  const initNotificationService = (config: NotificationEventConfig) => {
    const topicHandlers: Partial<Record<NotificationTopic, (message?: unknown) => void>> = {}

    if (platformConfig?.notificationService?.events) {
      platformConfig.notificationService.events.forEach((event) => {
        topicHandlers[event] = (message) => {
          if (message == null) {
            return
          }

          dispatchNotificationEvent(event, message as NotificationPayload<typeof event>)
        }
      })
    }

    notificationService = new NotificationSocketService(
      config,
      topicHandlers as Record<string, (message?: unknown) => void>
    )
    notificationService.connect()

    if (config.authToken) {
      notificationService.authenticate(config.authToken)
    }
  }

  const getNotificationService = () => {
    if (!notificationService) {
      throw new Error(
        'Notification service instance is not defined. You must call `initNotificationService` first.'
      )
    }
    return notificationService
  }

  const disconnectFromNotificationService = () => {
    getNotificationService().disconnect()
  }

  const authenticateNotificationService = (authToken: string) => {
    getNotificationService().authenticate(authToken)
  }

  return {
    initNotificationService,
    disconnectFromNotificationService,
    authenticateNotificationService
  }
}
